import '../css/app.css';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { ProducaoRoute } from '@/components/ProducaoRoute';
import AppLayout from '@/Layouts/AppLayout';
import PagePreloader from '@/Components/navigation/PagePreloader';
const LayoutSettings = lazy(() => import('@/Pages/LayoutSettings'));

// Paginas publicas
import Login from '@/Pages/Login';
import AuthCallback from '@/Pages/AuthCallback';
import NotFound from '@/Pages/NotFound';
import PlaceholderPage from '@/Pages/PlaceholderPage';

// Paginas do modulo Industrializacao (producao)
import ProducaoDashboard from '@/Pages/producao/Dashboard';
import Produtos from '@/Pages/producao/Produtos';
import ProdutoForm from '@/Pages/producao/ProdutoForm';
import CentroTrabalho from '@/Pages/producao/CentroTrabalho';
import CentroTrabalhoForm from '@/Pages/producao/CentroTrabalhoForm';
import Responsaveis from '@/Pages/producao/Responsaveis';
import ResponsavelForm from '@/Pages/producao/ResponsavelForm';
import UnidadesMedida from '@/Pages/producao/UnidadesMedida';
import UnidadeMedidaForm from '@/Pages/producao/UnidadeMedidaForm';
import FichaTecnica from '@/Pages/producao/FichaTecnica';
import FichaTecnicaForm from '@/Pages/producao/FichaTecnicaForm';
import Roteiro from '@/Pages/producao/Roteiro';
import RoteiroForm from '@/Pages/producao/RoteiroForm';
import OrdemProducao from '@/Pages/producao/OrdemProducao';
import OrdemProducaoForm from '@/Pages/producao/OrdemProducaoForm';
import Apontamentos from '@/Pages/producao/Apontamentos';
import ApontamentoForm from '@/Pages/producao/ApontamentoForm';
import Qualidade from '@/Pages/producao/Qualidade';
import QualidadeForm from '@/Pages/producao/QualidadeForm';
import Custos from '@/Pages/producao/Custos';
import Locais from '@/Pages/producao/Locais';
import LocalForm from '@/Pages/producao/LocalForm';
import Veiculos from '@/Pages/producao/Veiculos';
import VeiculoForm from '@/Pages/producao/VeiculoForm';
import Lotes from '@/Pages/producao/Lotes';
import LoteForm from '@/Pages/producao/LoteForm';
import Movimentacoes from '@/Pages/producao/Movimentacoes';
import MovimentacaoForm from '@/Pages/producao/MovimentacaoForm';
import Rastreabilidade from '@/Pages/producao/Rastreabilidade';
import RomaneioPage from '@/Pages/producao/Romaneio';
import RomaneioForm from '@/Pages/producao/RomaneioForm';
import RelatoriosPage from '@/Pages/producao/Relatorios';

// Paginas administrativas
import AdminDashboard from '@/Pages/admin/Dashboard';
import AdminEmpresas from '@/Pages/admin/Empresas';
import AdminUsuarios from '@/Pages/admin/Usuarios';
import UsuarioForm from '@/Pages/admin/UsuarioForm';
import AdminLogs from '@/Pages/admin/Logs';
import AdminSincronizacaoLogs from '@/Pages/admin/SincronizacaoLogs';

// Paginas core compartilhadas
import AccountSettings from '@/Pages/AccountSettings';
import Empresas from '@/Pages/Empresas';
import EmpresaForm from '@/Pages/EmpresaForm';
import Register from '@/Pages/Register';

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/auth-callback', element: <AuthCallback /> },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/producao/dashboard" replace /> },
          { path: 'dashboard', element: <Navigate to="/producao/dashboard" replace /> },
          { path: 'account-settings', element: <AccountSettings /> },
          { path: 'layout', element: <Suspense fallback={<PagePreloader fullScreen={false} />}><LayoutSettings /></Suspense> },
          { path: 'empresas', element: <Empresas /> },
          { path: 'empresas/novo', element: <EmpresaForm /> },
          { path: 'empresas/:id/editar', element: <EmpresaForm /> },
          { path: 'register', element: <Register /> },
          { path: 'requests', element: <PlaceholderPage title="Solicitações" /> },

          // ===== MÓDULO INDUSTRIALIZAÇÃO/PRODUÇÃO =====
          {
            element: <ProducaoRoute />,
            children: [
              { path: 'producao/dashboard', element: <ProducaoDashboard /> },

              // Cadastros
              { path: 'producao/produtos', element: <Produtos /> },
              { path: 'producao/produtos/novo', element: <ProdutoForm /> },
              { path: 'producao/produtos/:id/editar', element: <ProdutoForm /> },
              { path: 'producao/centro-trabalho', element: <CentroTrabalho /> },
              { path: 'producao/centro-trabalho/novo', element: <CentroTrabalhoForm /> },
              { path: 'producao/centro-trabalho/:id/editar', element: <CentroTrabalhoForm /> },
              { path: 'producao/responsaveis', element: <Responsaveis /> },
              { path: 'producao/responsaveis/novo', element: <ResponsavelForm /> },
              { path: 'producao/responsaveis/:id/editar', element: <ResponsavelForm /> },
              { path: 'producao/locais', element: <Locais /> },
              { path: 'producao/locais/novo', element: <LocalForm /> },
              { path: 'producao/locais/:id/editar', element: <LocalForm /> },
              { path: 'producao/veiculos', element: <Veiculos /> },
              { path: 'producao/veiculos/novo', element: <VeiculoForm /> },
              { path: 'producao/veiculos/:id/editar', element: <VeiculoForm /> },
              { path: 'producao/unidades-medida', element: <UnidadesMedida /> },
              { path: 'producao/unidades-medida/novo', element: <UnidadeMedidaForm /> },
              { path: 'producao/unidades-medida/:id/editar', element: <UnidadeMedidaForm /> },

              // Engenharia
              { path: 'producao/fichas-tecnicas', element: <FichaTecnica /> },
              { path: 'producao/fichas-tecnicas/novo', element: <FichaTecnicaForm /> },
              { path: 'producao/fichas-tecnicas/:id/visualizar', element: <FichaTecnicaForm /> },
              { path: 'producao/fichas-tecnicas/:id/editar', element: <FichaTecnicaForm /> },
              { path: 'producao/roteiros', element: <Roteiro /> },
              { path: 'producao/roteiros/novo', element: <RoteiroForm /> },
              { path: 'producao/roteiros/:id/editar', element: <RoteiroForm /> },

              // PCP/Ordens de Producao
              { path: 'producao/ordens', element: <OrdemProducao /> },
              { path: 'producao/ordens/nova', element: <OrdemProducaoForm /> },
              { path: 'producao/ordens/:id/visualizar', element: <OrdemProducaoForm /> },
              { path: 'producao/ordens/:id/editar', element: <OrdemProducaoForm /> },
              { path: 'producao/ordens/:id', element: <Navigate to="/producao/ordens" replace /> },
              { path: 'producao/apontamentos', element: <Apontamentos /> },
              { path: 'producao/apontamentos/novo', element: <ApontamentoForm /> },

              // Qualidade
              { path: 'producao/qualidade', element: <Qualidade /> },
              { path: 'producao/qualidade/novo', element: <QualidadeForm /> },
              { path: 'producao/qualidade/:id/editar', element: <QualidadeForm /> },

              // Custos
              { path: 'producao/custos', element: <Custos /> },

              // Estoque
              { path: 'producao/estoque/lotes', element: <Lotes /> },
              { path: 'producao/estoque/lotes/novo', element: <LoteForm /> },
              { path: 'producao/estoque/lotes/:id/visualizar', element: <LoteForm /> },
              { path: 'producao/estoque/lotes/:id/editar', element: <LoteForm /> },
              { path: 'producao/estoque/movimentacoes', element: <Movimentacoes /> },
              { path: 'producao/estoque/movimentacoes/novo', element: <MovimentacaoForm /> },
              { path: 'producao/estoque/movimentacoes/:id/visualizar', element: <MovimentacaoForm /> },

              // Rastreabilidade
              { path: 'producao/rastreabilidade', element: <Rastreabilidade /> },

              // Romaneios
              { path: 'producao/romaneios', element: <RomaneioPage /> },
              { path: 'producao/romaneios/novo', element: <RomaneioForm /> },
              { path: 'producao/romaneios/:id/visualizar', element: <RomaneioForm /> },

              // Relatorios
              { path: 'producao/relatorios', element: <RelatoriosPage /> },
            ],
          },

          // ===== MÓDULO ADMINISTRAÇÃO =====
          {
            element: <AdminRoute />,
            children: [
              { path: 'admin/dashboard', element: <AdminDashboard /> },
              { path: 'admin/empresas', element: <AdminEmpresas /> },
              { path: 'admin/empresas/novo', element: <EmpresaForm backPath="/admin/empresas" admin /> },
              { path: 'admin/empresas/:id/editar', element: <EmpresaForm backPath="/admin/empresas" admin /> },
              { path: 'admin/usuarios', element: <AdminUsuarios /> },
              { path: 'admin/usuarios/novo', element: <UsuarioForm /> },
              { path: 'admin/usuarios/:id/editar', element: <UsuarioForm /> },
              { path: 'admin/logs', element: <AdminLogs /> },
              { path: 'admin/sincronizacao-logs', element: <AdminSincronizacaoLogs /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);

const root = document.getElementById('app');

if (!root) {
  throw new Error('Root element not found');
}

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </StrictMode>
);
